/**
 * Tutorial overlay controller. Shows the scripted step panel (rules/tutorial.ts
 * TUT_STEPS) over the board and advances it: action steps wait for the game to
 * report an event via `event(...)` (the game calls this from select/move/end-
 * move/new-turn), while text steps ("next"/"done") advance on the panel button.
 * Ported from the legacy v2 tut_show / tut_event / tut_advance flow.
 *
 * UI-only; the step data and gating triggers are pure (rules/tutorial.ts).
 */
import { TUT_STEPS, type TutorialTrigger } from '../rules/tutorial';
import { ICON_VP } from './icons';

const CSS = `
.ar-tut { position: absolute; left: 12px; bottom: 12px; width: min(320px, calc(100% - 24px)); z-index: 25;
  background: linear-gradient(180deg, hsl(45, 67.3%, 90.4%), hsl(44, 53.7%, 83.9%) 45%, hsl(40, 46.9%, 71.2%));
  border: 2px solid hsl(31, 47.6%, 28.4%); border-top: 4px solid hsl(43, 63.2%, 58.4%); border-radius: 6px;
  box-shadow: 0 6px 22px hsla(0, 0%, 0%, 0.45); font-family: Georgia, 'Times New Roman', serif; color: hsl(28, 55.6%, 10.6%);
  padding: 12px 14px; line-height: 1.4; }
.ar-tut h3 { margin: 0 0 5px; color: hsl(23, 66.7%, 21.2%); font-size: 15px; }
.ar-tut .step { color: hsl(31, 47.6%, 28.4%); font-style: italic; font-size: 11.5px; margin-bottom: 4px; }
.ar-tut p { margin: 0 0 10px; font-size: 13.5px; line-height: 1.5; }
.ar-tut p b { color: hsl(23, 66.7%, 21.2%); }
.ar-tut .row { display: flex; gap: 7px; justify-content: flex-end; }
.ar-tut .ar-btn { font-family: inherit; font-weight: 700; font-size: 13px; padding: 6px 12px; cursor: pointer; color: hsl(28, 55.6%, 10.6%);
  background: linear-gradient(180deg, hsl(46, 80.7%, 73.5%), hsl(43, 63.2%, 58.4%) 55%, hsl(42, 61.6%, 34.7%)); border: 2px solid hsl(30, 54.2%, 18.8%); border-radius: 4px;
  box-shadow: inset 0 1px 0 hsla(0, 0%, 100%, 0.5); text-shadow: 0 1px 0 hsla(0, 0%, 100%, 0.35); }
.ar-tut .ar-btn.primary { color: hsl(90, 100%, 95.7%); background: linear-gradient(180deg, hsl(105, 47.8%, 64.7%), hsl(105, 38.4%, 43.9%) 55%, hsl(105, 41.7%, 29.6%)); border-color: hsl(110, 40.6%, 25.1%); text-shadow: 0 1px 0 hsla(0, 0%, 0%, 0.3); }
.ar-tut .ar-btn.ghost { background: linear-gradient(180deg, hsl(44, 58.4%, 84.9%), hsl(40, 46.9%, 71.2%)); font-weight: 400; }
.ar-tut .waiting { color: hsl(31, 47.6%, 28.4%); font-size: 12px; font-style: italic; align-self: center; margin-right: auto; }
`;

export interface TutorialController {
  /** the game reports a player action; advances the panel if it's what we await */
  event(ev: TutorialTrigger): void;
  /** current step index (for the E2E) */
  step(): number;
  active(): boolean;
  destroy(): void;
}

export interface TutorialOpts {
  container: HTMLElement;
  /** player abandoned the lesson */
  onSkip: () => void;
  /** the scripted steps were all completed (player clicked "let me play") */
  onComplete?: () => void;
}

export function startTutorial(opts: TutorialOpts): TutorialController {
  if (!document.getElementById('ar-tut-style')) {
    const style = document.createElement('style');
    style.id = 'ar-tut-style';
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  const panel = document.createElement('div');
  panel.className = 'ar-tut';
  panel.id = 'ar-tut';
  opts.container.appendChild(panel);

  let step = 0;
  let alive = true;

  function render(): void {
    if (!alive) return;
    if (step >= TUT_STEPS.length) {
      panel.style.display = 'none';
      return;
    }
    const st = TUT_STEPS[step]!;
    const showNext = st.when === 'next' || st.when === 'done';
    panel.innerHTML = `
      <div class="step">Step ${step + 1} of ${TUT_STEPS.length}</div>
      <h3>${st.title}</h3>
      <p>${st.text.replaceAll('{VP}', ICON_VP)}</p>
      <div class="row">
        ${showNext ? '' : '<span class="waiting">…do the action above to continue</span>'}
        <button class="ar-btn ghost" id="ar-tut-skip">Skip tutorial</button>
        ${showNext ? `<button class="ar-btn primary" id="ar-tut-next">${st.when === 'done' ? 'Got it — let me play!' : 'Next ▸'}</button>` : ''}
      </div>`;
    panel.querySelector('#ar-tut-skip')?.addEventListener('click', () => {
      finish(false);
    });
    panel.querySelector('#ar-tut-next')?.addEventListener('click', () => {
      if (st.when === 'done') finish(true);
      else advance();
    });
  }

  function advance(): void {
    step++;
    render();
  }

  function finish(completed: boolean): void {
    alive = false;
    panel.remove();
    if (completed) opts.onComplete?.();
    else opts.onSkip();
  }

  render();

  return {
    event(ev) {
      if (!alive || step >= TUT_STEPS.length) return;
      if (TUT_STEPS[step]!.when === ev) advance();
    },
    step: () => step,
    active: () => alive && step < TUT_STEPS.length,
    destroy() {
      alive = false;
      panel.remove();
    },
  };
}
